import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import type {
  TestRequirement,
  RequirementType,
  Tag,
  Paginated,
} from "@/common/types/generated";
import { TableConfig } from "@/config/appConfig";

interface QueryParams {
  versionId: number;
  projectId?: number;
  folderId?: number;
  /** @deprecated use folderId */
  testCaseFolderId?: number;
  page?: number;
  size?: number;
  query?: string;
  search?: string;
  sort?: string;
  /** When true, a selected folder also returns requirements from nested folders. */
  recursive?: boolean;
}

const buildQueryParams = (params: QueryParams): string => {
  const {
    versionId,
    folderId,
    testCaseFolderId,
    page = 0,
    size = TableConfig.ITEMS_PER_PAGE,
    query = "",
    search,
    sort = "name,asc",
    recursive,
  } = params;

  const resolvedFolderId = folderId ?? testCaseFolderId;
  const searchParams = new URLSearchParams({
    versionId: String(versionId),
    page: String(page),
    size: String(size),
    query,
    sort,
  });
  if (resolvedFolderId != null)
    searchParams.set("folderId", String(resolvedFolderId));
  if (search) searchParams.set("search", search);
  if (recursive) searchParams.set("recursive", "true");

  return `?${searchParams.toString()}`;
};

type GenericQueryParams = {
  query?: string;
  sort?: string;
  page?: number;
  size?: number;
  [key: string]: string | number | boolean | null | undefined;
};

export const buildSpecialQueryParams = (params: GenericQueryParams): string => {
  const { query, ...rest } = params;

  const searchParams = new URLSearchParams();

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.set(key, String(value));
    }
  });

  // Manually prepend `query` if present
  const queryPart = query ? `query=${query}` : "";

  return `?${[queryPart, searchParams.toString()].filter(Boolean).join("&")}`;
};

export const createrequirement = (
  payload: Partial<TestRequirement>,
  files?: File[],
) => {
  const formData = new FormData();

  // Add the defect object as JSON blob under 'defectlog' part
  formData.append(
    "requirement",
    new Blob([JSON.stringify(payload)], { type: "application/json" }),
  );

  // Optionally add files
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append("files", file);
    });
  }

  return httpClient<void>(
    httpMethods.POST,
    API_ENDPOINTS.TESTCASE.testRequirement,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data", // Let the browser set the boundary
      },
    },
  );
};

export const fetchAllRequirements = (params: QueryParams) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testRequirement}${buildQueryParams(params)}`;
  return httpClient<Paginated<TestRequirement>>(httpMethods.GET, endpoint);
};
export const fetchRequirementById = (id: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testRequirement}${id}`;
  return httpClient<TestRequirement>(httpMethods.GET, endpoint);
};

/** Batch-fetch requirements by id in a single request (avoids per-row N+1). */
export const fetchRequirementsByIds = (ids: number[], versionId?: number) => {
  const searchParams = new URLSearchParams();
  searchParams.set("ids", ids.join(","));
  searchParams.set("size", String(ids.length || 1));
  if (versionId) searchParams.set("versionId", String(versionId));
  const endpoint = `${API_ENDPOINTS.TESTCASE.testRequirement}?${searchParams.toString()}`;
  return httpClient<Paginated<TestRequirement>>(httpMethods.GET, endpoint);
};
export const fetchAllRequirementTags = async () => {
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.requirementTags}`;
  const response = await httpClient<any>(httpMethods.GET, endpoint);
  const data = response?.data ?? response ?? [];
  // Backend returns a paginated object { content: Tag[] } — extract the array
  return Array.isArray(data) ? data : (data?.content ?? []);
};

export const fetchRequirementTagsById = async (requirementId: number) => {
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.requirementTags}/associate_item/${requirementId}`;
  const response = await httpClient<Tag[]>(httpMethods.GET, endpoint);
  return response?.data ?? response ?? [];
};

export const associateRequirementTags = (
  requirementId: number,
  tagNames: string[],
) =>
  httpClient<Tag[]>(
    httpMethods.POST,
    `${API_ENDPOINTS.REQUIREMENT.requirementTags}/associate_item/${requirementId}`,
    tagNames,
  );

export const fetchRequirementAttachments = async (
  requirementId: number,
): Promise<any[]> => {
  const response = await httpClient<any[]>(
    httpMethods.GET,
    `${API_ENDPOINTS.ATTACHMENT.attachments}/REQUIREMENT/${requirementId}`,
  );
  const payload: any = response?.data ?? response;

  // httpClient returns { data, status, statusText }, while the endpoint returns
  // the attachment array directly.
  return Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.content)
      ? payload.content
      : [];
};

export const uploadRequirementAttachment = (
  requirementId: number,
  file: File,
) => {
  const formData = new FormData();
  formData.append("fileContent", file);
  formData.append("name", file.name);
  formData.append("entityId", String(requirementId));
  formData.append("moduleName", "REQUIREMENT");
  formData.append("entity", "REQUIREMENT");
  return httpClient<any>(
    httpMethods.MULTIPART,
    API_ENDPOINTS.ATTACHMENT.attachments,
    formData,
  );
};

export const deleteRequirementAttachment = (attachmentId: number) =>
  httpClient<void>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.ATTACHMENT.attachments}/${attachmentId}`,
  );
export const fetchRequirementtypes = async (
  params: Pick<QueryParams, "projectId">,
) => {
  const searchParams = new URLSearchParams();
  if (params.projectId) searchParams.set("projectId", String(params.projectId));
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.requirement}?${searchParams.toString()}`;
  const response = await httpClient<Paginated<RequirementType>>(
    httpMethods.GET,
    endpoint,
  );
  return response.data.content; // ✅ Just return the array
};
// services/defectlogs.api.ts
export const updaterequirement = (
  id: number,
  payload: Partial<TestRequirement>,
  files?: File[],
) => {
  const formData = new FormData();

  // 👇 Key must match backend: "requirement"
  formData.append(
    "requirement",
    new Blob([JSON.stringify(payload)], { type: "application/json" }),
  );

  // 👇 Use "file" (not "files") to match @RequestParam("file")
  if (files && files.length > 0) {
    files.forEach((file) => formData.append("file", file));
  }

  return httpClient<void>(
    httpMethods.PUT,
    `${API_ENDPOINTS.TESTCASE.testRequirement}${id}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" }, // Browser will auto-fill boundary
    },
  );
};

export const deleterequirement = (id: number) => {
  return httpClient<void>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.TESTCASE.testRequirement}${id}`,
  );
};

export const checkRequirementNameExists = (
  name: string,
  workspaceVersionId: number,
  folderId: number | null,
) => {
  const params = new URLSearchParams({
    name,
    workspaceVersionId: String(workspaceVersionId),
    folderId: folderId == null ? "" : String(folderId),
  });
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.checkNameExists}?${params.toString()}`;
  return httpClient(httpMethods.GET, endpoint);
};

// ── Linked Test Cases (test cases that reference this requirement) ────────

export const fetchTestCasesByRequirementId = (requirementId: number) => {
  const params = new URLSearchParams({
    requirementId: String(requirementId),
    deleted: "false",
    size: "200",
  });
  return httpClient<{ content: any[]; totalElements: number }>(
    httpMethods.GET,
    `${API_ENDPOINTS.TESTCASE.testCase}?${params.toString()}`,
  );
};

// ── Linked Requirements ───────────────────────────────────────────────────

export const fetchRequirementLinks = (requirementId: number) =>
  httpClient<number[]>(
    httpMethods.GET,
    API_ENDPOINTS.REQUIREMENT.requirementLinks(requirementId),
  );

export const addRequirementLink = (requirementId: number, linkedId: number) =>
  httpClient<void>(
    httpMethods.POST,
    `${API_ENDPOINTS.REQUIREMENT.requirementLinks(requirementId)}/${linkedId}`,
  );

export const removeRequirementLink = (
  requirementId: number,
  linkedId: number,
) =>
  httpClient<void>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.REQUIREMENT.requirementLinks(requirementId)}/${linkedId}`,
  );

// ── MVP: approval, traceability, coverage, bulk, import/export (Table B) ─────

export interface RequirementCoverage {
  status: string;
  coveragePct: number;
  totalCases: number;
  passed: number;
  failed: number;
  blocked: number;
  notRun: number;
  openDefects: number;
}

export interface RequirementTrace {
  requirement_id: number;
  coverage: (RequirementCoverage & { coverage_status?: string }) | null;
  cases: {
    test_case_id: number;
    test_case_name: string;
    latest_status: string;
    cycle_id: number | null;
  }[];
  defects: {
    id: number;
    title: string;
    status: string;
    priority?: string;
    severity?: string;
    defect_key?: string;
    test_case_id: number | null;
  }[];
}

export interface CoverageGapReport {
  total_gaps: number;
  not_covered: number;
  partial: number;
  critical_gaps: number;
  gaps: {
    requirement_id: number;
    name: string;
    priority: string | null;
    approval_status: string;
    coverage_status: string;
    coverage_pct: number;
    total_cases: number;
    open_defects: number;
  }[];
}

export interface CoverageSummary {
  total: number;
  covered: number;
  uncovered: number;
  at_risk: number;
  passed: number;
  failed: number;
  coverage_pct: number;
}

export interface BulkRequirementResult {
  updated: number;
  failed: { id: number; error: string }[];
}

export interface ImportRequirementResult {
  dryRun: boolean;
  total: number;
  valid: number;
  created: number;
  errors: { row: number; field?: string; error: string }[];
}

export const approveRequirement = (id: number) =>
  httpClient<{ message: string; approvalStatus: string }>(
    httpMethods.POST,
    API_ENDPOINTS.REQUIREMENT.approve(id),
  );

export const rejectRequirement = (id: number, reason: string) =>
  httpClient<{ message: string; approvalStatus: string }>(
    httpMethods.POST,
    API_ENDPOINTS.REQUIREMENT.reject(id),
    { reason },
  );

export const changeRequirementApprovalStatus = (
  id: number,
  status: string,
  reason?: string,
) =>
  httpClient<{ message: string; approvalStatus: string }>(
    httpMethods.POST,
    API_ENDPOINTS.REQUIREMENT.approvalStatus(id),
    { status, reason },
  );

export const fetchRequirementTrace = (id: number) =>
  httpClient<RequirementTrace>(
    httpMethods.GET,
    API_ENDPOINTS.REQUIREMENT.trace(id),
  );

export const fetchCoverageGaps = (versionId: number) =>
  httpClient<CoverageGapReport>(
    httpMethods.GET,
    `${API_ENDPOINTS.REQUIREMENT.coverageGaps}?versionId=${versionId}`,
  );

export const fetchCoverageSummary = (versionId: number) =>
  httpClient<CoverageSummary>(
    httpMethods.GET,
    `${API_ENDPOINTS.REQUIREMENT.coverageSummary}?versionId=${versionId}`,
  );

// ── Group B: audit history, activity timeline, dashboard ───────────────────

export interface AuditEntry {
  id: number;
  requirement_id: number;
  change_type: string;
  field_name: string | null;
  from_value: string | null;
  to_value: string | null;
  actor_user_id: number | null;
  actor_name: string | null;
  note: string | null;
  timestamp: string | null;
}

export interface ActivityItem {
  kind: "audit" | "comment";
  at: string;
  actor_user_id: number | null;
  actor_name: string | null;
  // audit
  change_type?: string;
  field_name?: string | null;
  from_value?: string | null;
  to_value?: string | null;
  // comment
  comment_id?: number;
  text?: string;
  pinned?: boolean;
}

export interface RequirementDashboard {
  version_id: number;
  total: number;
  by_approval_status: Record<string, number>;
  by_priority: Record<string, number>;
  coverage: {
    not_covered: number;
    covered: number;
    partial: number;
    passed: number;
    failed: number;
    blocked: number;
  };
  with_open_defects: number;
  coverage_pct: number;
  volatility: { changed_7d: number; changed_30d: number };
}

export const fetchRequirementAudit = (
  id: number,
  params?: { page?: number; size?: number },
) => {
  const sp = new URLSearchParams();
  if (params?.page != null) sp.set("page", String(params.page));
  if (params?.size != null) sp.set("size", String(params.size));
  const suffix = sp.toString() ? `?${sp.toString()}` : "";
  return httpClient<{
    content: AuditEntry[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }>(httpMethods.GET, `${API_ENDPOINTS.REQUIREMENT.audit(id)}${suffix}`);
};

export const fetchRequirementActivity = (id: number, limit = 100) =>
  httpClient<{ items: ActivityItem[]; total: number }>(
    httpMethods.GET,
    `${API_ENDPOINTS.REQUIREMENT.activity(id)}?limit=${limit}`,
  );

export const fetchRequirementDashboard = (versionId: number) =>
  httpClient<RequirementDashboard>(
    httpMethods.GET,
    `${API_ENDPOINTS.REQUIREMENT.dashboard}?versionId=${versionId}`,
  );

export const bulkRequirements = (
  action: "update" | "delete" | "move",
  ids: number[],
  payload?: Record<string, unknown>,
) =>
  httpClient<BulkRequirementResult>(
    httpMethods.POST,
    API_ENDPOINTS.REQUIREMENT.bulk,
    {
      action,
      ids,
      payload,
    },
  );

export interface BulkCreateRequirementResult {
  createdCount: number;
  skippedCount?: number;
  errorCount: number;
  created: TestRequirement[];
  skipped?: { index: number; name?: string; reason: string }[];
  errors: { index: number; name?: string; message: string }[];
}

export const bulkCreateRequirements = (body: {
  requirements: Partial<TestRequirement>[];
  workspaceVersionId: number;
  projectId?: number;
  folderId?: number;
  userId?: number;
}) =>
  httpClient<BulkCreateRequirementResult>(
    httpMethods.POST,
    API_ENDPOINTS.REQUIREMENT.bulkCreate,
    body,
    // Bulk creates many rows + resolves folders server-side; allow more time
    // than the default 30s so the client doesn't time out mid-write.
    { timeout: 120000 },
  );

export const importRequirements = (
  file: File,
  workspaceVersionId: number,
  opts?: {
    projectId?: number;
    folderId?: number;
    dryRun?: boolean;
    columnMapping?: Record<string, string>;
  },
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("workspaceVersionId", String(workspaceVersionId));
  if (opts?.projectId) formData.append("projectId", String(opts.projectId));
  if (opts?.folderId) formData.append("folderId", String(opts.folderId));
  if (opts?.columnMapping) {
    formData.append("columnMapping", JSON.stringify(opts.columnMapping));
  }
  const suffix = opts?.dryRun ? "?dryRun=true" : "";
  return httpClient<ImportRequirementResult>(
    httpMethods.MULTIPART,
    `${API_ENDPOINTS.REQUIREMENT.import}${suffix}`,
    formData,
  );
};

export const exportRequirements = async (
  params: Record<string, string | number>,
): Promise<void> => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => sp.set(k, String(v)));
  const endpoint = `${API_ENDPOINTS.REQUIREMENT.export}?${sp.toString()}`;

  const response = await httpClient<Blob>(
    httpMethods.GET,
    endpoint,
    undefined,
    {
      responseType: "blob",
    },
  );

  const url = URL.createObjectURL(response.data as unknown as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "requirements.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
